import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { APPLICATION_LINK_PROPS } from "../config/applicationForm";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/podcast", label: "Podcast" },
  { to: "/speaking", label: "Speaking" },
  { to: "/consulting", label: "Consulting" },
  { to: "/books", label: "Books" },
  { to: "/contact", label: "Contact" },
];

const Monogram = () => (
  <svg
    width="44"
    height="56"
    viewBox="0 0 44 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="0.75" y="0.75" width="42.5" height="54.5" stroke="#C9A961" strokeWidth="1.2" />
    <text
      x="50%"
      y="58%"
      dominantBaseline="middle"
      textAnchor="middle"
      fontFamily="Playfair Display, serif"
      fontStyle="italic"
      fontSize="26"
      fill="#E8EEF6"
    >
      IO
    </text>
  </svg>
);

const Navbar = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-ink/90 backdrop-blur-md border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-3.5 flex items-center justify-between gap-6">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-4 group" data-testid="brand-link">
          <Monogram />
          <div className="leading-tight">
            <div className="font-serif text-[16px] md:text-[18px] tracking-[0.18em] text-white">
              IYARE OMORUYI
            </div>
            <div className="text-[9px] md:text-[10px] tracking-[0.32em] text-gold/90 mt-1">
              EDUCATOR. SPEAKER. MURABBI.
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-active={pathname === l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`nav-underline text-[11px] tracking-[0.28em] font-medium uppercase transition-colors ${
                pathname === l.to ? "text-white" : "text-white/75 hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <a
            {...APPLICATION_LINK_PROPS}
            data-testid="nav-link-apply"
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.28em] font-medium uppercase text-azure-soft hover:text-white transition-colors"
          >
            Apply
            <ExternalLink className="w-3 h-3" strokeWidth={1.8} />
          </a>
        </nav>

        {/* CTA */}
        <Link
          to="/contact"
          data-testid="nav-book-iyare-btn"
          className="hidden lg:inline-flex items-center text-[10.5px] tracking-[0.32em] uppercase border border-white/70 hover:border-gold hover:text-gold text-white px-5 py-2.5 transition-all duration-300"
        >
          Book Iyare
        </Link>

        {/* Mobile burger */}
        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-white"
        >
          <div className="space-y-1.5">
            <span className={`block w-6 h-px bg-white transition ${open ? "translate-y-[6px] rotate-45" : ""}`} />
            <span className={`block w-6 h-px bg-white transition ${open ? "opacity-0" : ""}`} />
            <span className={`block w-6 h-px bg-white transition ${open ? "-translate-y-[6px] -rotate-45" : ""}`} />
          </div>
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden bg-ink-deep/98 backdrop-blur-md border-t border-white/5">
          <nav className="px-6 py-6 flex flex-col gap-5" aria-label="Mobile">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={`text-sm tracking-[0.28em] uppercase ${
                  pathname === l.to ? "text-gold" : "text-white/80"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <a
              {...APPLICATION_LINK_PROPS}
              data-testid="mobile-nav-link-apply"
              className="inline-flex items-center gap-2 text-sm tracking-[0.28em] uppercase text-azure-soft"
            >
              Apply
              <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.8} />
            </a>
            <Link
              to="/contact"
              data-testid="mobile-nav-book-iyare-btn"
              className="mt-2 inline-flex items-center justify-center text-[11px] tracking-[0.32em] uppercase border border-gold text-gold px-5 py-3"
            >
              Book Iyare
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
